import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Space,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { AuthStatus } from '../../api/MarketplaceAuth';

const { Text, Paragraph } = Typography;

type Props = {
  marketplace: string;
  data: AuthStatus;
  onSave: (fields: {
    sellerId?: string;
    apiToken?: string;
    cookie?: string;
  }) => Promise<void>;
  onCheck: () => Promise<unknown>;
};

function sellerHint(marketplace: string) {
  return marketplace === 'wildberries'
    ? 'Идентификатор продавца WB — можно найти в личном кабинете.'
    : 'Можно найти в личном кабинете Ozon в правой верхней части страницы.';
}

function maskSecret(v: string) {
  if (v.length <= 6) return '••••••';
  return `••••••••••••••••••••••••••••••••••${v.slice(-3)}`;
}

export const AccessSettingsTab = ({
  marketplace,
  data,
  onSave,
  onCheck,
}: Props) => {
  const [sellerId, setSellerId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [cookie, setCookie] = useState('');
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    setSellerId('');
    setApiToken('');
    setCookie('');
  }, [data.markerplace]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        sellerId: sellerId.trim() || undefined,
        apiToken: apiToken.trim() || undefined,
        cookie: cookie.trim() || undefined,
      });
      setSellerId('');
      setApiToken('');
      setCookie('');
      message.success('Сохранено');
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const check = async () => {
    setChecking(true);
    try {
      const res: any = await onCheck();
      if (res?.warehouses != null) {
        message.success(`Подключение ок · складов МП: ${res.warehouses}`);
      } else if (res?.products != null) {
        message.success(`Подключение ок · товаров в каталоге: ${res.products}`);
      } else {
        message.success('Подключение проверено');
      }
    } catch (e: any) {
      message.error(e?.message || 'Проверка не удалась');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Токены и cookie используются для API маркетплейса. Оставьте поле пустым,
        чтобы не менять текущее значение.
      </Paragraph>

      <Form layout="vertical">
        <Form.Item
          label={
            <Space size={4}>
              Seller ID
              <Tooltip title={sellerHint(marketplace)}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </Space>
          }
        >
          <Input
            placeholder={data.sellerId || 'Не задан'}
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
          />
          {data.sellerId ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Текущее: {data.sellerId}
            </Text>
          ) : null}
        </Form.Item>

        <Form.Item label="API-токен">
          <Input.Password
            placeholder={
              data.apiToken
                ? maskSecret(data.apiToken)
                : 'Введите новый токен'
            }
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            visibilityToggle
          />
        </Form.Item>

        <Form.Item label="Cookie">
          <Input.Password
            placeholder={
              data.cookie ? maskSecret(data.cookie) : 'Введите новую cookie'
            }
            value={cookie}
            onChange={(e) => setCookie(e.target.value)}
            visibilityToggle
          />
        </Form.Item>

        <Space style={{ marginTop: 8 }}>
          <Button loading={checking} onClick={() => void check()}>
            Проверить подключение
          </Button>
          <Button type="primary" loading={saving} onClick={() => void save()}>
            Сохранить изменения
          </Button>
        </Space>
      </Form>
    </div>
  );
};
